import { createContext, useContext } from 'react'
import type { BackButtonConfig, PageAction, PageTabsConfig } from './types'

export interface TabCounts {
	[key: string]: number
}

export interface PageContextType {
	actions: PageAction[] // array of actions for the page header
	setPageActions: (actions: PageAction[]) => void
	updatePageAction: (actionId: string, updates: Partial<PageAction>) => void // Update action properties like loading, disabled, etc.
	clearPageActions: () => void // Clear all actions from the context
	tabCounts?: TabCounts
	setTabCounts?: (counts: TabCounts) => void
	dynamicTabs?: PageTabsConfig | null // dynamic tabs for the page header (for alert dashboard)
	setPageTabs?: (tabs: PageTabsConfig | null) => void
	clearPageTabs?: () => void
	backButton?: BackButtonConfig | null // back button configuration
	setBackButton?: (config: BackButtonConfig | null) => void // Set back button configuration
	clearBackButton?: () => void // Clear back button configuration
	hideHeader?: boolean // flag to hide the entire page header
	setHideHeader?: (hide: boolean) => void // Set hide header flag
	hideCustomViews?: boolean // flag to hide the custom views section in header
	setHideCustomViews?: (hide: boolean) => void // Set hide custom views flag
	/**
	 * Element the header exposes beside its CTA slot for a page that has no CTAs and would rather put its
	 * own controls there (see `usePageHeaderControls`). Null until the header has mounted it.
	 *
	 * A DOM slot rather than a node in this context on purpose: the page keeps rendering its controls in its
	 * own subtree and only portals them here, so their state, hooks and effects are untouched by the move.
	 */
	headerControlsSlot?: HTMLElement | null
	setHeaderControlsSlot?: (element: HTMLElement | null) => void
}

// Component-free module keeps this context's identity stable across Fast Refresh;
// co-locating it with a component export makes HMR mint a new context and desync the provider.
export const PageContext = createContext<PageContextType | undefined>(undefined)

export function usePageContext(): PageContextType {
	const context = useContext(PageContext)

	if (context === undefined) {
		throw new Error('usePageContext must be used within a PageProvider')
	}

	return context
}
